import { createContext, useEffect, useState } from "react";
import { queryKeys } from "../react-query/constants";
import { getLoginToken, getStoredUser, setStoredUser } from "../storage";
import { getDecodedJWT, isAuthenticated } from "../utils";
import { useAuthenticatedUser } from "./hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext({
  user: undefined,
  token: "",
  isAuthenticated: false,
  authenticate: (token) => {},
  logout: () => {},
  updateUser: (data) => {},
  openDrawer: false,
  toggleDrawer: () => {},
  cart: [],
  updateCart: () => {},
  addToCart: () => {},
  wishList: [],
  updateWishList: () => {},
  addToWishList: () => {},
  openWishList: false,
  toggleWishList: () => {},
  buyNow: () => {},
  convertToCart: () => {},
});

function AuthContextProvider({ children }) {
  const [authToken, setAuthToken] = useState(undefined);
  const [user, setUser] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [wishList, setWishList] = useState([]);
  const userDetails = useAuthenticatedUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated()) {
      logout();
    }
  }, []);

  useEffect(() => {
    const data = getLoginToken();
    if (data) {
      setAuthToken(data);
    }
  }, []);

  useEffect(() => {
    const data = getStoredUser();
    if (data) {
      setUser(data);
    }
  }, []);

  // console.log(userDetails, "check");

  useEffect(() => {
    if (userDetails) {
      setUser(userDetails);
    }
  }, [userDetails]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("cart") || "[]");
    const list = JSON.parse(localStorage.getItem("wishList") || "[]");
    setCart(items);
    setWishList(list);
  }, []);

  // consolet.log(userDetails, "user");
  function logout() {
    setUser(undefined);
    setAuthToken(undefined);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    queryClient.invalidateQueries([queryKeys.user]);
  }
  function updateUser(data) {
    setUser(data);
  }
  function updateCart(data) {
    setCart(data);
  }
  function updateWishList(data) {
    setWishList(data);
  }
  function authenticate(data) {
    setAuthToken(data);
    const decoded = getDecodedJWT();
    setUser(decoded);
    setStoredUser(decoded);
  }

  function toggleDrawer() {
    setOpen(!open);
  }

  function toggleWishList() {
    setIsOpen(!isOpen);
  }

  const addToCart = (items) => {
    const isItemAlreadyAdded = cart.some((item) => item._id === items?._id);

    if (isItemAlreadyAdded) {
      setIsOpen(false);
      setOpen(true);
      return;
    }

    const newItem = {
      ...items,
      qty: 1,
    };

    const updatedCart = [...cart, newItem];
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setCart(updatedCart);
    setIsOpen(false);
    setOpen(true);
  };

  const buyNow = (items) => {
    const isItemAlreadyAdded = cart.some((item) => item._id === items?._id);

    if (isItemAlreadyAdded) {
      navigate("/app/order");
    }

    const newItem = {
      ...items,
      qty: 1,
    };

    const updatedCart = [...cart, newItem];
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setCart(updatedCart);
    navigate("/app/order");
  };

  const addToWishList = (items) => {
    const isItemAlreadyAdded = wishList.some((item) => item._id === items?._id);

    if (isItemAlreadyAdded) {
      setOpen(false);
      setIsOpen(true);
      return;
    }

    const newItem = {
      ...items,
      qty: 1,
    };

    const updatedCart = [...wishList, newItem];
    localStorage.setItem("wishList", JSON.stringify(updatedCart));
    setWishList(updatedCart);
    setOpen(false);
    setIsOpen(true);
  };

  const convertToCart = (items) => {
    localStorage.setItem("cart", JSON.stringify(items));
    setCart(items);
    localStorage.removeItem("wishList");
    setWishList([]);
  };
  const value = {
    user: user,
    token: authToken,
    isAuthenticated: !!authToken,
    authenticate: authenticate,
    logout: logout,
    updateUser: updateUser,
    openDrawer: open,
    toggleDrawer: toggleDrawer,
    cart: cart,
    updateCart: updateCart,
    addToCart: addToCart,
    wishList: wishList,
    updateWishList: updateWishList,
    addToWishList: addToWishList,
    openWishList: isOpen,
    toggleWishList: toggleWishList,
    buyNow: buyNow,
    convertToCart,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContextProvider;
